import type {
  FilterDefinition,
  ListQueryCodecConfig,
  ListQueryState,
  PaginationState,
  SortState,
} from "./types";

const positiveInteger = (value: string | null, fallback: number): number => {
  const parsed = Number.parseInt(value || "", 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
};

const getParameterNames = <Filters>(config: ListQueryCodecConfig<Filters>) => ({
  search: config.searchParameter || "q",
  sort: config.sortParameter || "sort",
  sortDirection: config.sortDirectionParameter || "direction",
  page: config.pageParameter || "page",
  pageSize: config.pageSizeParameter || "pageSize",
  cursor: config.cursorParameter || "cursor",
});

const parseSort = <Filters>(
  params: URLSearchParams,
  config: ListQueryCodecConfig<Filters>,
): SortState | null => {
  const names = getParameterNames(config);
  const key = (params.get(names.sort) || "").trim();
  if (!key) {
    return config.defaultSort || null;
  }

  return {
    key,
    direction: params.get(names.sortDirection) === "desc" ? "desc" : "asc",
  };
};

const parsePagination = <Filters>(
  params: URLSearchParams,
  config: ListQueryCodecConfig<Filters>,
): PaginationState => {
  const names = getParameterNames(config);
  const defaults = config.defaultPagination;

  if (defaults.mode === "page") {
    return {
      mode: "page",
      page: positiveInteger(params.get(names.page), defaults.page),
      pageSize: positiveInteger(params.get(names.pageSize), defaults.pageSize),
    };
  }

  if (defaults.mode === "cursor") {
    const cursor = (params.get(names.cursor) || "").trim();
    return cursor ? { mode: "cursor", cursor } : { mode: "cursor" };
  }

  return { mode: "none" };
};

export const parseListQueryState = <Filters>(
  params: URLSearchParams,
  config: ListQueryCodecConfig<Filters>,
): ListQueryState<Filters> => {
  const names = getParameterNames(config);
  const filters = { ...config.defaultFilters } as Filters;

  config.filters.forEach((definition) => {
    const parameter = definition.parameter || definition.key;
    const values = params.getAll(parameter);
    const parsed = values.length ? definition.parse(values) : definition.defaultValue;
    filters[definition.key] = definition.normalize(parsed) as Filters[typeof definition.key];
  });

  return {
    search: (params.get(names.search) || "").trim(),
    filters,
    sort: parseSort(params, config),
    pagination: parsePagination(params, config),
  };
};

const clearManagedParameters = <Filters>(
  params: URLSearchParams,
  config: ListQueryCodecConfig<Filters>,
) => {
  const names = getParameterNames(config);
  [
    names.search,
    names.sort,
    names.sortDirection,
    names.page,
    names.pageSize,
    names.cursor,
  ].forEach((name) => params.delete(name));
  config.filters.forEach((definition) =>
    params.delete(definition.parameter || definition.key),
  );
};

export const serializeListQueryState = <Filters>(
  state: ListQueryState<Filters>,
  config: ListQueryCodecConfig<Filters>,
  currentParams: URLSearchParams = new URLSearchParams(),
): URLSearchParams => {
  const params = new URLSearchParams(currentParams);
  const names = getParameterNames(config);
  clearManagedParameters(params, config);

  const search = state.search.trim();
  if (search) {
    params.set(names.search, search);
  }

  config.filters.forEach((definition) => {
    const value = definition.normalize(state.filters[definition.key]);
    if (!definition.isActive(value)) {
      return;
    }

    const parameter = definition.parameter || definition.key;
    definition.serialize(value).forEach((serializedValue) => {
      if (serializedValue.length) {
        params.append(parameter, serializedValue);
      }
    });
  });

  const isDefaultSort = Boolean(
    state.sort &&
    config.defaultSort &&
    state.sort.key === config.defaultSort.key &&
    state.sort.direction === config.defaultSort.direction,
  );
  if (state.sort && !isDefaultSort) {
    params.set(names.sort, state.sort.key);
    params.set(names.sortDirection, state.sort.direction);
  }

  if (state.pagination.mode === "page") {
    const defaultPage = config.defaultPagination.mode === "page"
      ? config.defaultPagination.page
      : 1;
    const defaultPageSize = config.defaultPagination.mode === "page"
      ? config.defaultPagination.pageSize
      : state.pagination.pageSize;

    if (state.pagination.page !== defaultPage) {
      params.set(names.page, `${state.pagination.page}`);
    }
    if (state.pagination.pageSize !== defaultPageSize) {
      params.set(names.pageSize, `${state.pagination.pageSize}`);
    }
  } else if (state.pagination.mode === "cursor" && state.pagination.cursor) {
    params.set(names.cursor, state.pagination.cursor);
  }

  params.sort();
  return params;
};

const stableValue = (value: unknown): unknown => {
  if (Array.isArray(value)) {
    return value.map(stableValue);
  }

  if (value && typeof value === "object") {
    return Object.keys(value as Record<string, unknown>)
      .sort()
      .reduce<Record<string, unknown>>((result, key) => {
        const nextValue = (value as Record<string, unknown>)[key];
        if (typeof nextValue !== "undefined") {
          result[key] = stableValue(nextValue);
        }
        return result;
      }, {});
  }

  return value;
};

export const stableListStateFingerprint = <Filters>(
  state: ListQueryState<Filters>,
): string => JSON.stringify(stableValue(state));

export const buildListQueryKey = <Filters>(
  resource: string,
  operatorContext: string | number | null,
  state: ListQueryState<Filters>,
) => [
  resource,
  operatorContext === null ? "all-operators" : `${operatorContext}`,
  stableListStateFingerprint(state),
] as const;

export const reconcileSelection = ({
  selectedIds,
  previousQueryFingerprint,
  nextQueryFingerprint,
  validIds,
  retainAcrossQuery = false,
}: {
  selectedIds: string[];
  previousQueryFingerprint: string;
  nextQueryFingerprint: string;
  validIds?: string[];
  retainAcrossQuery?: boolean;
}): string[] => {
  if (!retainAcrossQuery && previousQueryFingerprint !== nextQueryFingerprint) {
    return [];
  }

  if (!validIds) {
    return Array.from(new Set(selectedIds));
  }

  const valid = new Set(validIds);
  return Array.from(new Set(selectedIds)).filter((id) => valid.has(id));
};
